import styled from 'styled-components';
import Lightning from '../assets/images/icon-lightning.svg';
import Button from '../components/Button';
import {Form, Field} from 'react-final-form';
import * as Validation from '../utils/form-validation';
import Picture from '../assets/images/picture-01.svg';
import axios from 'axios';
import ErrorPicture from '../assets/images/picture-02.svg';
import {useEffect, useState} from 'react';
import {getCustomerPublicInfo, loginHr} from '../utils/api';
import {useTranslation} from 'react-i18next';

import {
  FormSubtitle,
  FormTitle,
  FormInput,
  FormCheckbox,
} from '../components/FormElements';
import {useParams} from 'react-router-dom';
import Header from '../components/Header';
import {Main} from '../components/Layout';
import ChatBot from '../components/Chatbot';

type FormValues = {
  email: string;
  ['privacy-consens']: boolean;
};

export default function DashboardLogin() {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [customer, setCustomer] = useState<HRCustomerPublicInfo>();

  const {t} = useTranslation();
  const {customerId} = useParams();

  useEffect(() => {
    if (!customerId) {
      return;
    }

    const getAndSetCustomerInfo = async () => {
      try {
        const r = await getCustomerPublicInfo(customerId);
        setCustomer(r);
      } catch (err) {
        console.log(err);
      }
    };

    getAndSetCustomerInfo();
  }, [customerId]);

  const handleSubmit = async (values: FormValues) => {
    let email = values.email;

    try {
      await loginHr(customerId || '', email);

      setSuccess(true);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.data.token) {
          setError(t('errors.generic-error'));
        } else {
          setError(t(err.response?.data.message));
        }
      } else {
        throw err;
      }
    }
  };

  const TheForm = () => {
    return (
      <>
        <FormTitle>{t('login-dashboard.form.title')}</FormTitle>
        <FormSubtitle>{t('login-dashboard.form.subtitle')}</FormSubtitle>
        <Form
          onSubmit={handleSubmit}
          initialValues={{}}
          render={({handleSubmit, submitting}) => {
            return (
              <form onSubmit={handleSubmit}>
                <Field
                  name="email"
                  component={FormInput}
                  placeholder={t('login-dashboard.form.email')}
                  validate={Validation.requiredEmail}
                />
                <Field
                  name="privacy-consens"
                  type="checkbox"
                  component={FormCheckbox}
                  validate={Validation.required}
                  label={t('login-dashboard.form.privacy')}
                />
                <div style={{height: '20px'}}></div>
                <Button loading={submitting} type="submit">
                  {t('login-dashboard.form.submit')}
                </Button>
              </form>
            );
          }}
        ></Form>
      </>
    );
  };

  const renderForm = () => {
    if (success) {
      return (
        <FormSuccess>
          <img src={Picture} alt="Success" />
          <p>{t('login-dashboard.success')}</p>
          <a href="##" target="_blank" rel="noopener noreferrer">
            {t('login-dashboard.success-no-link')}
          </a>
        </FormSuccess>
      );
    }

    if (error) {
      return (
        <ErrorContainer>
          <img src={ErrorPicture} alt="error" />
          <Error>{error}</Error>
        </ErrorContainer>
      );
    }

    return <TheForm />;
  };

  return (
    <>
      <Header clientLogo={customer?.logo} clientName={customer?.name} />
      <Main>
        <Container>
          <Inner>
            <Left>
              {customer?.logo && (
                <ClientLogoBig
                  src={`${process.env.REACT_APP_BASE_URL}/uploads/images/customer/${customer.logo}`}
                  alt={customer?.name}
                />
              )}
              <h1>{t('login-dashboard.title')}</h1>
              <p
                dangerouslySetInnerHTML={{
                  __html: t('login-dashboard.subtitle'),
                }}
              ></p>
            </Left>
            <Right>
              <Box>
                <LightningImage src={Lightning} alt="Lightning" />
                <BoxInner>
                  <FormWrapper>{renderForm()}</FormWrapper>
                </BoxInner>
              </Box>
            </Right>
          </Inner>
        </Container>
      </Main>
      <ChatBot />
    </>
  );
}

const Container = styled.div`
  max-width: ${(props) => props.theme.sizes.mw};
  margin: 0 auto;
  min-height: 100%;
  padding: 50px 40px;

  @media ${(props) => props.theme.breakpoints.phone} {
    padding: 20px 0;
  }

  h1 {
    font-size: 3.5rem;
    font-family: 'Bossa Bold', sans-serif;
    color: ${(props) => props.theme.colors.violet500};
    margin-bottom: 24px;

    @media ${(props) => props.theme.breakpoints.tablet} {
      font-size: 3rem;
    }
  }
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 100%;
  flex-wrap: wrap;
`;

const Left = styled.div`
  width: 40%;

  @media ${(props) => props.theme.breakpoints.tablet} {
    width: 100%;
    margin-bottom: 44px;
    padding: 20px;
  }
`;

const Right = styled.div`
  width: 50%;

  @media ${(props) => props.theme.breakpoints.tablet} {
    width: 100%;
  }
`;

const Box = styled.div`
  position: relative;
`;

const BoxInner = styled.div`
  width: 100%;
  min-height: 100%;
  background-color: ${(props) => props.theme.colors.orange100};
  padding: 150px 120px 150px 100px;
  clip-path: polygon(2% 0, 100% 5%, 100% 100%, 0 90%);

  @media ${(props) => props.theme.breakpoints.landscape} {
    padding: 120px 60px 120px 50px;
  }

  @media ${(props) => props.theme.breakpoints.phone} {
    clip-path: none;
    padding: 50px 20px;
  }
`;

const LightningImage = styled.img`
  width: 50px;
  position: absolute;
  top: 0;
  right: 10%;
  z-index: 1;

  @media ${(props) => props.theme.breakpoints.tablet} {
    top: -20px;
    width: 30px;
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
`;

const FormSuccess = styled.div`
  img {
    display: block;
    width: 100%;
    max-width: 300px;
    margin-bottom: 24px;
  }
  p {
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 44px;
  }
`;

const ErrorContainer = styled.div`
  img {
    display: block;
    width: 100%;
    max-width: 250px;
    margin-bottom: 2rem;
  }
`;

const Error = styled.h2`
  line-height: 1.4;
`;

const ClientLogoBig = styled.img`
  display: block;
  width: 100%;
  max-width: 16.25rem;
  margin-bottom: 2rem;

  @media ${(props) => props.theme.breakpoints.tablet} {
    display: none;
  }
`;
